import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  archiveNotificationsApi,
  deleteNotificationsApi,
  getNotificationSettingsApi,
  getNotificationsApi,
  getUnreadCountApi,
  markNotificationsReadApi,
  updateNotificationSettingsApi,
} from '~/api/notifications';
import type { Notification, NotificationSettings } from '~/types/notifications';
import { withoutMandatoryDisabled } from '~/utils/notificationTarget';

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([]);
  const unreadCount = ref(0);
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const hasMore = ref(false);
  const pagination = ref({
    page: 1,
    page_size: 20,
    total_count: 0,
    total_pages: 0,
  });
  const settings = ref<NotificationSettings | null>(null);
  const isContentModalOpen = ref(false);
  const activeNotification = ref<Notification | null>(null);
  /** 刪除結果已知但回讀列表／未讀數失敗時為 true，代表目前列表可能與伺服器不同步 */
  const isListStale = ref(false);

  const chunkArray = <T>(items: T[], size: number): T[][] => {
    if (size <= 0) return [items];
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  };

  const fetchAllNotificationUuids = async (): Promise<string[]> => {
    const uuids = new Set<string>();
    const pageSize = 100;
    let page = 1;
    let totalPages = 1;

    do {
      const response = await getNotificationsApi({
        page,
        page_size: pageSize,
      });
      for (const notification of response.notifications) {
        uuids.add(notification.uuid);
      }
      totalPages = response.pagination.total_pages;
      page += 1;
    } while (page <= totalPages);

    return [...uuids];
  };

  /**
   * 獲取通知列表
   */
  const fetchNotifications = async (params?: {
    type?: string;
    is_read?: boolean;
    is_archived?: boolean;
    page?: number;
    page_size?: number;
    append?: boolean; // 是否追加到現有列表
  }) => {
    try {
      const isFirstPage = !params?.append && (!params?.page || params.page === 1);

      if (isFirstPage) {
        isLoading.value = true;
      } else {
        isLoadingMore.value = true;
      }

      const response = await getNotificationsApi({
        ...params,
      });

      if (params?.append) {
        // 追加模式：將新通知追加到現有列表
        notifications.value.push(...response.notifications);
      } else {
        // 替換模式：替換整個列表
        notifications.value = response.notifications;
        isListStale.value = false;
      }

      pagination.value = response.pagination;
      // 判斷是否還有更多資料
      hasMore.value = response.pagination.page < response.pagination.total_pages;

      return response;
    } catch (error) {
      console.error('獲取通知列表失敗:', error);
      throw error;
    } finally {
      isLoading.value = false;
      isLoadingMore.value = false;
    }
  };

  /**
   * 載入更多通知（無限滾動）
   */
  const loadMoreNotifications = async () => {
    if (isLoadingMore.value || !hasMore.value) {
      return;
    }

    const nextPage = pagination.value.page + 1;
    await fetchNotifications({
      page: nextPage,
      page_size: pagination.value.page_size,
      append: true,
    });
  };

  /**
   * 獲取未讀通知數量
   */
  const fetchUnreadCount = async () => {
    try {
      const response = await getUnreadCountApi();
      unreadCount.value = response.unread_count;
      return response.unread_count;
    } catch (error) {
      console.error('獲取未讀數量失敗:', error);
      throw error;
    }
  };

  /**
   * 標記通知為已讀
   */
  const markAsRead = async (uuids: string[]) => {
    // 儲存原始狀態，以便失敗時回滾
    const originalStates = notifications.value
      .filter((n) => uuids.includes(n.uuid))
      .map((n) => ({
        uuid: n.uuid,
        is_read: n.is_read,
        read_at: n.read_at,
      }));

    // Optimistic update: 先更新本地狀態
    for (const notification of notifications.value) {
      if (uuids.includes(notification.uuid)) {
        notification.is_read = true;
        notification.read_at = Date.now();
      }
    }

    // 更新未讀數量（optimistic）
    const affectedCount = originalStates.filter((s) => !s.is_read).length;
    unreadCount.value = Math.max(0, unreadCount.value - affectedCount);

    try {
      const response = await markNotificationsReadApi({ uuids });
      // API 成功後，重新獲取未讀數量以確保同步
      // await fetchUnreadCount();
      return response;
    } catch (error) {
      console.error('標記已讀失敗:', error);
      // 回滾狀態
      for (const originalState of originalStates) {
        const notification = notifications.value.find((n) => n.uuid === originalState.uuid);
        if (notification) {
          notification.is_read = originalState.is_read;
          notification.read_at = originalState.read_at;
        }
      }
      // 回滾未讀數量
      await fetchUnreadCount();
      throw error;
    }
  };

  /**
   * 標記所有通知為已讀
   */
  const markAllAsRead = async () => {
    const unreadUuids = notifications.value.filter((n) => !n.is_read).map((n) => n.uuid);
    if (unreadUuids.length > 0) {
      await markAsRead(unreadUuids);
    }
  };

  /**
   * 把確定還在伺服器上的通知放回列表（依 uuid 去重），並補回未讀數。
   * 只用於回讀失敗時：已確認刪除成功的 uuid 不可傳進來。
   */
  const restoreKnownRemaining = (rows: Notification[]) => {
    const present = new Set(notifications.value.map((n) => n.uuid));
    const missing = rows.filter((n) => !present.has(n.uuid));
    if (missing.length === 0) return;
    notifications.value = [...notifications.value, ...missing].sort(
      (a, b) => b.created_at - a.created_at
    );
    unreadCount.value += missing.filter((n) => !n.is_read).length;
  };

  /** 未讀數同步只是輔助，失敗不影響刪除結果 */
  const syncUnreadCount = async () => {
    try {
      await fetchUnreadCount();
    } catch {
      isListStale.value = true;
    }
  };

  /**
   * 刪除通知
   */
  const deleteNotifications = async (uuids: string[]) => {
    // 儲存被刪除的通知，以便失敗時回滾
    const deletedNotifications = notifications.value.filter((n) => uuids.includes(n.uuid));

    // 計算被刪除的未讀通知數量
    const deletedUnreadCount = deletedNotifications.filter((n) => !n.is_read).length;

    // Optimistic update: 先從本地狀態中移除
    notifications.value = notifications.value.filter(
      (notification) => !uuids.includes(notification.uuid)
    );

    // 更新未讀數量（optimistic）
    unreadCount.value = Math.max(0, unreadCount.value - deletedUnreadCount);

    let response: Awaited<ReturnType<typeof deleteNotificationsApi>>;
    try {
      response = await deleteNotificationsApi({ uuids });
    } catch (error) {
      console.error('刪除通知失敗:', error);
      // DELETE 本身失敗：沒有任何已知結果，整批回滾
      restoreKnownRemaining(deletedNotifications);
      await syncUnreadCount();
      throw error;
    }

    // 以下 DELETE 結果已知：成功的 uuid 不再放回，回讀失敗也不重送 DELETE
    if (response.failed_count > 0) {
      try {
        await fetchNotifications({ page: 1, page_size: pagination.value.page_size });
      } catch (error) {
        console.error('刪除後重新讀取通知失敗:', error);
        const failedUuids = new Set(response.failed_uuids ?? []);
        restoreKnownRemaining(deletedNotifications.filter((n) => failedUuids.has(n.uuid)));
        isListStale.value = true;
      }
    }
    await syncUnreadCount();
    return response;
  };

  /**
   * 刪除所有通知
   */
  const deleteAllNotifications = async () => {
    // 儲存所有通知與狀態，以便失敗時回滾
    const allNotifications = [...notifications.value];
    const originalUnreadCount = unreadCount.value;
    const originalPagination = { ...pagination.value };
    const originalHasMore = hasMore.value;

    // Optimistic update: 先清空列表（UI 立即回應）
    notifications.value = [];
    unreadCount.value = 0;
    hasMore.value = false;
    pagination.value = {
      ...pagination.value,
      page: 1,
      total_count: 0,
      total_pages: 0,
    };

    let allUuids: string[];
    try {
      allUuids = await fetchAllNotificationUuids();
    } catch (error) {
      console.error('刪除所有通知失敗:', error);
      // 還沒送出任何 DELETE：整個回滾
      notifications.value = allNotifications;
      unreadCount.value = originalUnreadCount;
      pagination.value = originalPagination;
      hasMore.value = originalHasMore;
      await syncUnreadCount();
      throw error;
    }

    if (allUuids.length === 0) {
      // 伺服器已無通知，未讀數已由 optimistic update 設為 0，僅嘗試同步
      await syncUnreadCount();
      return {
        success_count: 0,
        failed_count: 0,
        failed_uuids: [],
      };
    }

    let successCount = 0;
    let failedCount = 0;
    const failedUuids: string[] = [];
    const confirmedDeleted = new Set<string>();
    let mutationError: unknown = null;

    for (const batch of chunkArray(allUuids, 100)) {
      try {
        const response = await deleteNotificationsApi({ uuids: batch });
        successCount += response?.success_count ?? 0;
        failedCount += response?.failed_count ?? 0;
        const batchFailed = Array.isArray(response?.failed_uuids) ? response.failed_uuids : [];
        failedUuids.push(...batchFailed);
        for (const uuid of batch) {
          if (!batchFailed.includes(uuid)) confirmedDeleted.add(uuid);
        }
      } catch (error) {
        // 這批結果未知；停止送後續批次，也不重送
        console.error('刪除所有通知失敗:', error);
        mutationError = error;
        break;
      }
    }

    if (failedCount > 0 || mutationError) {
      try {
        await fetchNotifications({ page: 1, page_size: originalPagination.page_size });
      } catch (error) {
        console.error('刪除後重新讀取通知失敗:', error);
        // 只放回不確定是否已刪除的列；已確認刪除成功的保持移除
        pagination.value = originalPagination;
        hasMore.value = originalHasMore;
        restoreKnownRemaining(allNotifications.filter((n) => !confirmedDeleted.has(n.uuid)));
        isListStale.value = true;
      }
    }
    await syncUnreadCount();

    if (mutationError) throw mutationError;
    return {
      success_count: successCount,
      failed_count: failedCount,
      failed_uuids: failedUuids,
    };
  };

  /**
   * 歸檔通知
   */
  const archiveNotifications = async (uuids: string[]) => {
    try {
      const response = await archiveNotificationsApi({ uuids });
      // 更新本地狀態
      for (const notification of notifications.value) {
        if (uuids.includes(notification.uuid)) {
          notification.is_archived = true;
          notification.archived_at = Date.now();
        }
      }
      return response;
    } catch (error) {
      console.error('歸檔通知失敗:', error);
      throw error;
    }
  };

  /**
   * 獲取通知設置
   */
  const fetchSettings = async () => {
    try {
      const response = await getNotificationSettingsApi();
      settings.value = response;
      return response;
    } catch (error) {
      console.error('獲取通知設置失敗:', error);
      throw error;
    }
  };

  /**
   * 更新通知設置
   */
  const updateSettings = async (enabledTypes: string[], disabledTypes: string[]) => {
    try {
      const response = await updateNotificationSettingsApi({
        enabled_types: enabledTypes,
        // 強制送達類型（如 approval_pending）不可關閉
        disabled_types: withoutMandatoryDisabled(disabledTypes),
      });
      settings.value = response;
      return response;
    } catch (error) {
      console.error('更新通知設置失敗:', error);
      throw error;
    }
  };

  /**
   * 計算屬性：未讀通知列表
   */
  const unreadNotifications = computed(() => notifications.value.filter((n) => !n.is_read));

  /**
   * 計算屬性：已讀通知列表
   */
  const readNotifications = computed(() => notifications.value.filter((n) => n.is_read));

  /**
   * 重置狀態
   */
  const reset = () => {
    notifications.value = [];
    unreadCount.value = 0;
    isLoading.value = false;
    isLoadingMore.value = false;
    hasMore.value = false;
    pagination.value = {
      page: 1,
      page_size: 20,
      total_count: 0,
      total_pages: 0,
    };
    settings.value = null;
    isListStale.value = false;
    isContentModalOpen.value = false;
    activeNotification.value = null;
  };

  /**
   * 開啟通知內容視窗
   */
  const openContentModal = (notification: Notification) => {
    activeNotification.value = notification;
    isContentModalOpen.value = true;
  };

  /**
   * 關閉通知內容視窗
   */
  const closeContentModal = () => {
    isContentModalOpen.value = false;
    // activeNotification.value = null;
  };

  return {
    notifications,
    unreadCount,
    isLoading,
    isLoadingMore,
    hasMore,
    pagination,
    settings,
    isContentModalOpen,
    isListStale,
    activeNotification,
    unreadNotifications,
    readNotifications,
    fetchNotifications,
    loadMoreNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotifications,
    deleteAllNotifications,
    archiveNotifications,
    fetchSettings,
    updateSettings,
    openContentModal,
    closeContentModal,
    reset,
  };
});
